<template>
  <div v-if="sessionsPlanner.size" v-dragscroll class="pa-4 full-height">
    <v-row>
      <v-col cols="2" style="padding: 0 8px;">
        <div class="full-height">
          <div class="height-26"></div>
          <div class="padding-12">
            <span>Planning</span>
            <v-icon size="28" color="black" class="px-2 py-1" @click.stop="handleExportation()">mdi-export</v-icon>
          </div>
          <div v-if="isHorseTab" class="ml-1 padding-12">
            <span class="font-size-12">Catégories de taille</span>
            <v-row class="mt-2 mb-2" no-gutters>
              <div
                v-for="(category, horseCategoryIndex) in horseCategories"
                :key="horseCategoryIndex"
                class="d-flex flex-column align-center"
              >
                <div class="mr-2">
                  <span class="font-size-14">{{ category.label }}</span>
                </div>
                <v-checkbox
                  class="mt-0"
                  color="third"
                  :input-value="horseCategories[horseCategoryIndex].checkbox"
                  hide-details
                  @change="updateHorseCategoryCheckBox(horseCategories, horseCategoryIndex)"
                />
              </div>
            </v-row>
          </div>
        </div>
      </v-col>
      <v-col cols="10" class="pr-5" style="max-height: 212px;">
        <div
          id="mainPlanner"
          v-dragscroll
          class="full-height overflow-x-auto  all-scroll no-scroll"
          @dragscrollmove="syncScrollWith(`contentTypePlanner-${tab}`)"
        >
          <HoursList />
          <div class="timetable">
            <ul id="mainPlannerList" class="room-timeline grid-size pl2 overflow-x-hidden" style="overflow-y: scroll">
              <li
                v-for="(row, index) in sessionsPlanner.size"
                :key="`plannerRow_${index}`"
                @click="openCreateSessionForm($event)"
              >
                <div class="d-flex align-center full-height">
                  <v-tooltip
                    v-for="session in sessionsPlanner.get(row)"
                    :key="`mainPlannerItem_${session.uuid}`"
                    top
                  >
                    <template #activator="{ on, attrs }">
                      <span
                        :id="session.uuid"
                        data-no-dragscroll
                        class="session sessionHeight text-truncate d-flex justify-start align-center pl-2 z1"
                        :style="session.style"
                        draggable="true"
                        v-bind="attrs"
                        @dragstart="dragStart($event, session)"
                        @dragover.prevent="dragOver($event, session)"
                        @dragleave="dragLeave($event, session)"
                        @drop="dropItem($event, session)"
                        @click="handleSessionClick($event, session)"
                        v-on="on"
                      >
                        {{ label(session.name) }}
                      </span>
                    </template>
                    <p>{{ session.name }}</p>
                    <p>Cavaliers: {{ (session.participants || []).length }} / {{ session.max_participant || '--' }}</p>
                    <p>Chevaux: {{ sessionAffectedHorses(session) }}</p>
                  </v-tooltip>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </v-col>
    </v-row>
    <SessionCreate />
    <Modal v-if="showExportModal" :show="showExportModal" max-width="500" :close="closeExportModal" :show-dividers="false">
      <template #title>
        <div class="col-12">
          <h2 class="text-center third--text">Menu d'exportation</h2>
        </div>
      </template>
      <template #actions>
        <div class="d-flex flex flex-column justify-center px-4">
          <v-btn class="my-2 white--text" color="third" @click="exportSessionsData" :loading="horsesLoading"> Séances dans le planificateur </v-btn>
          <v-btn class="my-2 white--text" color="third" @click="exportHorseData(maxCsvRow)" :loading="horsesLoading"> Chevaux par sessions </v-btn>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { computed, inject, onMounted, watch, ref } from '@vue/composition-api';
import { differenceInMinutes, format, fromUnixTime, parseISO } from 'date-fns';
import { fr } from 'date-fns/esm/locale';
import { label, EventBus, sortArray, computeSessionMap, sendNotification, downloadCSV, getReferences, downloadExcel, syncScrollWith } from '@/helpers/utils';
import { getContractWithStock } from '@/helpers/request';
import { dragStart, dragLeave, dragOver, handleDrop } from '@/helpers/dragAndDrop';
import { startTimeOptions } from '@/helpers/dataManager';

import HoursList from './HoursList.vue';
import SessionCreate from '@/components/sessions/SessionCreate.vue';
import Modal from '@/components/common/Modal.vue';

import store from '@/store';

export default {
  components: { HoursList, SessionCreate, Modal },
  setup () {
    const sessions = computed(() => store.getters.dailySessions);
    const selectParticipantModalData = inject('selectParticipantModalData');
    const showParticipantModal = inject('showParticipantModal');
    const selectedSessionProduct = inject('selectedSessionProduct', null);
    const showExportModal = ref(false);
    const horsesLoading = ref(false);

    const maxCsvRow = ref(27);

    const currentWorkspace = computed(() => store.state.workspace);

    const horseCategories = ref([]);

    const selectedDate = computed(() => store.getters.selectedDate);

    const isHorseTab = computed(() => store.state.currentTab === 'horse');

    const horses = computed(() => store.state.horses);

    const openExportModal = async () => {
      showExportModal.value = true;
      if (!store.state.isLazyLoaded.horses) {
        horsesLoading.value = true;
        await store.dispatch('getHorses');
        horsesLoading.value = false;
      }
    };

    const closeExportModal = () => {
      showExportModal.value = false;
    };

    const getInstructorDetail = uuid => store.getters.instructor(uuid);

    const sessionsPlanner = computed(() => {
      const sessionsByTimeAsc = JSON.parse(JSON.stringify(sortArray(sessions.value, 'start_time')));
      const { sessionsMap } = computeSessionMap(sessionsByTimeAsc);

      return sessionsMap;
    });

    const tab = computed(() => store.state.currentTab);

    const computedStartTime = computed(() => {
      const times = startTimeOptions();

      return times.map(time => ({
        ...time,
        timestamp: new Date(`${selectedDate.value}T${time.value}`)
      }));
    });

    const handleSessionClick = (event, session) => {
      if (session) {
        EventBus.$emit('closeDrawerState');
        EventBus.$emit('showEditSession', { session: session });
      }
    };

    const openCreateSessionForm = event => {
      if (event.target.tagName.toLowerCase() === 'span') {
        return;
      }
      const mainPlanner = document.getElementById('mainPlannerList');
      const mainPlannerWidth = mainPlanner.getBoundingClientRect().width;

      // get 1h width
      const hour = (mainPlannerWidth * 6.25) / 100;
      const clickPositionX = event.clientX - mainPlanner.getBoundingClientRect().left;
      const startHour = 8;

      // get click hour
      const clickHour = clickPositionX / hour;
      // add the click hour to start hour(ex: if i click on 9h the hour variable get 1h)
      const hourCLick = startHour + parseInt(clickHour);

      const minutesWidth = clickHour.toFixed(2).split('.')[1];

      const minutesClick = minutesWidth / 0.16666666666;

      // const time = `${hourCLick.toString().padStart(2, '0')}:00`;

      const selectedTime = `${hourCLick.toString().padStart(2, '0')}:${Math.floor(Math.round(minutesClick) / 10).toString().padStart(2, '0')}`;
      const startTime = new Date(`${selectedDate.value}T${selectedTime}`);

      let timeDiff = computedStartTime.value.map(t => {
        const diff = Math.abs(differenceInMinutes(startTime, t.timestamp));

        return { ...t, diff };
      });

      timeDiff = timeDiff.sort((a, b) => (a.diff < b.diff ? -1 : 1));

      const closestTime = timeDiff[0];
      EventBus.$emit('closeDrawerState');
      EventBus.$emit('showSessionCreate', { time: closestTime.value });
    };

    const dropItem = async (event, contentType) => {
      const { isCustomerCase, customer_uuid, session, customer } = handleDrop(event, contentType);

      // old session customer dont have customer uuid so find it by horanet id
      const uuid = customer_uuid;
      if (!customer_uuid) {
        if (contentType.wtype === 'customer' && !uuid) {
          if (!uuid) {
            sendNotification('error', 'client introuvable');
            return;
          }
        }
      }

      if (isCustomerCase) {
        if (session.participants.find(participant => participant.uuid === customer.uuid)) {
          sendNotification('error', 'Le client existe déjà sur la session');
          return;
        }
        const text = 'Obtenir des contrats clients ...';
        sendNotification('info', text);
        const contractsWhithStock = await getContractWithStock(customer_uuid);
        selectParticipantModalData.value = { contracts: contractsWhithStock.data, user: customer, session: session };
        showParticipantModal.value = true;
      }
    };

    const exportSessionsData = () => {
      const sessionsByTimeAsc = JSON.parse(JSON.stringify(sortArray(sessions.value, 'start_time')));

      const sections = [];

      sections.push({
        headers: [],
        data: [{ text: `Feuille de monte du - ${format(parseISO(selectedDate.value), 'dd MMMM yyyy', { locale: fr })}` }]
      });

      sessionsByTimeAsc.map(session => {
        const sessionInstructors = (session.instructors || []).map(i => {
          const instructor = getInstructorDetail(i.uuid);

          if (!instructor) {
            return null;
          }

          return `${instructor.lastname} ${instructor.firstname}`;
        }).filter(instructor => instructor);
        const headers = [{ text: session.name }, { text: `De ${format(fromUnixTime(session.start_time), 'HH:mm')} à ${format(fromUnixTime(session.end_time), 'HH:mm')} - ${sessionInstructors.join(', ')}` }];

        const data = (session.participants || []).map(participant => ({
          participant: `${participant.firstname} ${participant.lastname} ${participant.presence_status === 'absent' ? ' (Absent)' : ''}`,
          horse: participant.horse ? store.getters.horse(participant.horse)?.horse_name : '--'
        }));

        const sessionHorses = (session.horses || []).filter(h => !(session.participants || []).some(p => p.horse === h.uuid)).map(horse => ({
          participant: '--',
          horse: store.getters.horse(horse.uuid)?.horse_name
        }));

        sections.push({ headers, data: [...data, ...sessionHorses] });
      });

      downloadCSV(sections, `Feuille de monte du - ${format(parseISO(selectedDate.value), 'dd MMMM yyyy', { locale: fr })}`);
      closeExportModal();
    };

    const exportHorseData = maxRows => {
      /* MaxRows should be the limit of rows in each page without counting with the headers row */
      const { dailySessions } = store.getters;

      const sessionsByTimeAsc = JSON.parse(JSON.stringify(sortArray(dailySessions, 'start_time')));

      const headers = [{ label: `${currentWorkspace.value.name} : ${format(new Date(), 'dd/MM/yyyy - EEEE', { locale: fr })}`, value: 'horseName' }];

      let data = [];

      (horses.value || []).map(horse => {
        data.push({ horseName: horse.wtitle, uuid: horse.uuid });
      });

      sessionsByTimeAsc.map(session => {
        const sessionParticipants = !session?.participants?.length ? ['-'] : session.participants.map(p => (`${p.lastname} ${p.firstname}`));
        headers.push({ label: `\n${sessionParticipants.join('\n')}\n\n${session.name} - ${format(new Date(session.start_time * 1000), 'HH:mm', { locale: fr })}`, value: session.uuid });
        data = (data || []).map(horse => {
          if (session.horses.some(h => h.uuid === horse.uuid)) {
            return ({ ...horse, [session.uuid]: 'X' });
          }
          return horse;
        });
      });

      const json = [];

      while (data.length) {
        const items = data.splice(0, maxRows);

        json.push({
          sheet: `Feuille ${json.length + 1}`,
          columns: headers,
          content: items
        });
      }

      downloadExcel(json, 'UCPA Pégase - Sessions d\'exportation de chevaux');

      closeExportModal();
    };

    const sideBarFilters = computed(() => store.getters.sideBarFilters);

    const updateHorseCategoryCheckBox = (category, index) => {
      horseCategories.value[index].checkbox = !horseCategories.value[index].checkbox;

      const selected = horseCategories.value.filter(c => c.checkbox);

      const filter = {
        ...sideBarFilters.value,
        horse: {
          ...sideBarFilters.value.horse,
          horse_size_category: selected.map(s => s.label)
        }
      };

      store.commit('setSideBarFilters', filter);
    };

    watch(sideBarFilters, () => {
      if (sideBarFilters.value && sideBarFilters.value.horse && sideBarFilters.value.horse.horse_size_category) {
        horseCategories.value = horseCategories.value.map(c => {
          c.checkbox = !!sideBarFilters.value.horse.horse_size_category.find(cat => cat === c.label);
          return c;
        });
      }
    });

    onMounted(() => {
      if (selectedSessionProduct.selectedSession && selectedSessionProduct.selectedSession.value) {
        handleSessionClick(null, selectedSessionProduct.selectedSession.value);

        selectedSessionProduct.changeSelectedSession(null);
      }

      const horseSizeCategories = getReferences('horseSizeCategory');
      horseCategories.value = horseSizeCategories.map(category => ({ label: category.label, checkbox: false }));
    });

    const sessionAffectedHorses = session => {
      const participantWithHorses = (session.participants || []).filter(p => p.horse);

      const remaningHorses = (session.horses || []).filter(h => h.uuid).length - participantWithHorses.length;

      return `${participantWithHorses.length}/${remaningHorses}`;
    };

    const handleExportation = () => {
      openExportModal();
    };

    return { maxCsvRow, sessionAffectedHorses, updateHorseCategoryCheckBox, isHorseTab, horseCategories, exportSessionsData, dropItem, openCreateSessionForm, handleSessionClick, sessionsPlanner, dragStart, dragLeave, dragOver, tab, label, showExportModal, openExportModal, closeExportModal, exportHorseData, handleExportation, horsesLoading, syncScrollWith };
  }
};
</script>
